import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import { productImage, onImgError } from '../utils/images';

interface CategorySuggestion {
  slug: string;
  name: string;
  image: string;
}

interface Suggestion {
  id: number;
  name: string;
  image: string;
  asin?: string;
  price: number;
  healthScore: number;
  category: string;
  category_name: string;
  handle?: string;
}

interface SearchBoxProps {
  initialQuery?: string;
  autoFocus?: boolean;
  large?: boolean;
}

// Debounced typeahead search with suggestion dropdown.
const SearchBox: React.FC<SearchBoxProps> = ({ initialQuery = '', autoFocus = false, large = false }) => {
  const [query, setQuery] = useState(initialQuery);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [catSuggestions, setCatSuggestions] = useState<CategorySuggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const boxRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => setQuery(initialQuery), [initialQuery]);

  useEffect(() => {
    if (query.trim().length < 2) {
      setSuggestions([]);
      setCatSuggestions([]);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const res = await fetch(`/api/search/suggest?q=${encodeURIComponent(query.trim())}`);
        if (res.ok) {
          const data = await res.json();
          // Backward compatible: old shape was a bare array
          if (Array.isArray(data)) {
            setSuggestions(data);
            setCatSuggestions([]);
          } else {
            setSuggestions(data.products || []);
            setCatSuggestions(data.categories || []);
          }
        }
      } catch {
        /* suggestions are best-effort */
      }
    }, 200);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const submit = (q: string) => {
    if (!q.trim()) return;
    setOpen(false);
    navigate(`/search?q=${encodeURIComponent(q.trim())}`);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, -1));
    } else if (e.key === 'Enter') {
      if (highlight >= 0 && suggestions[highlight]) {
        setOpen(false);
        navigate(`/products/${suggestions[highlight].handle || suggestions[highlight].id}`);
      } else {
        submit(query);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div ref={boxRef} className="relative w-full">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          submit(query);
        }}
        className="relative"
        role="search"
      >
        <input
          type="search"
          placeholder="Search clean products..."
          aria-label="Search products"
          aria-expanded={open && suggestions.length > 0}
          aria-controls="search-suggestions"
          role="combobox"
          autoFocus={autoFocus}
          className={`w-full ${large ? 'py-3.5 text-lg' : 'py-2.5'} pl-11 pr-4 text-earth-800 bg-white border border-earth-200 rounded-full focus:outline-none focus:ring-2 focus:ring-sage-500 focus:border-transparent transition-all duration-300 shadow-sm`}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
            setHighlight(-1);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
        />
        <div className="absolute inset-y-0 left-0 flex items-center pl-4 pointer-events-none">
          <FontAwesomeIcon icon={faSearch} className="w-4 h-4 text-earth-400" />
        </div>
      </form>

      {open && (suggestions.length > 0 || catSuggestions.length > 0) && (
        <ul
          id="search-suggestions"
          role="listbox"
          className="absolute z-30 left-0 right-0 mt-2 bg-white rounded-xl border border-earth-100 shadow-lg overflow-hidden max-h-[70vh] overflow-y-auto"
        >
          {catSuggestions.length > 0 && (
            <>
              <li className="px-4 pt-3 pb-1 text-[11px] font-semibold uppercase tracking-wider text-earth-400">Categories</li>
              {catSuggestions.map((c) => (
                <li key={c.slug} role="option" aria-selected={false}>
                  <button
                    type="button"
                    onClick={() => { setOpen(false); navigate(`/categories/${c.slug}`); }}
                    className="w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-sage-50 transition-colors"
                  >
                    <span className="text-sm font-medium text-sage-800">{c.name}</span>
                  </button>
                </li>
              ))}
              <li className="border-t border-earth-100" aria-hidden="true" />
            </>
          )}
          {suggestions.map((s, i) => (
            <li key={s.id} role="option" aria-selected={i === highlight}>
              <button
                type="button"
                onMouseEnter={() => setHighlight(i)}
                onClick={() => {
                  setOpen(false);
                  navigate(`/products/${s.handle || s.id}`);
                }}
                className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${
                  i === highlight ? 'bg-sage-50' : ''
                }`}
              >
                <img
                  src={productImage(s)}
                  alt=""
                  loading="lazy"
                  onError={onImgError()}
                  className="h-9 w-9 rounded-md object-cover bg-cream-200 flex-shrink-0"
                />
                <span className="flex-1 min-w-0">
                  <span className="block text-sm text-earth-800 truncate">{s.name}</span>
                  <span className="block text-xs text-earth-500">{s.category_name}</span>
                </span>
                {s.price > 0 && (
                  <span className="text-sm font-semibold text-sage-700">${s.price.toFixed(2)}</span>
                )}
              </button>
            </li>
          ))}
          <li>
            <button
              type="button"
              onClick={() => submit(query)}
              className="w-full px-4 py-2.5 text-sm text-sage-700 font-medium text-left hover:bg-sage-50 transition-colors border-t border-earth-100"
            >
              See all results for “{query.trim()}”
            </button>
          </li>
        </ul>
      )}
    </div>
  );
};

export default SearchBox;
